import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Plus, Download, Save, ArrowLeft, Trash2, Edit2, Images } from 'lucide-react';
import api from '../../services/api';
import { getReport, saveReport, addExpenseItem, updateExpenseItem, removeExpenseItem, receiptHref, listImports, cancelImport, dismissImport, confirmChecked, setExpenseRate, submitReport, approveReport, rejectReport, getAuditTrail } from '../../services/expenseService';
import ExpenseItemForm from '../../components/expense/ExpenseItemForm';
import ReceiptImportDialog from '../../components/expense/ReceiptImportDialog';

// The server's report as this page holds it (dates as yyyy-mm-dd for the inputs).
const toUiReport = (r) => ({
    ...r,
    dateRange: {
        start: r.startDate ? r.startDate.split('T')[0] : '',
        end: r.endDate ? r.endDate.split('T')[0] : ''
    },
    items: r.items || []
});

// An item change replaces the lines and the total only: whatever is typed in the title, dates or
// purpose is not saved yet and must stay.
const withServerItems = (prev, updated) => ({ ...prev, items: updated.items || [], totalAmount: updated.totalAmount, currency: updated.currency || prev.currency, rates: updated.rates || prev.rates || [] });

const sumItems = (items) => items.reduce((sum, item) => sum + (parseFloat(item.convertedAmount || item.amount) || 0), 0);

const ExpenseReportForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [report, setReport] = useState({
        title: '',
        dateRange: {
            start: new Date().toISOString().split('T')[0],
            end: new Date().toISOString().split('T')[0]
        },
        purpose: '',
        items: [],
        totalAmount: 0,
        status: 'DRAFT',
        currency: 'INR'
    });
    const [auditTrail, setAuditTrail] = useState([]);
    const [showItemForm, setShowItemForm] = useState(false);
    const [showBulk, setShowBulk] = useState(false);
    const [imports, setImports] = useState([]);
    const importsRef = useRef([]);
    const [pollFailures, setPollFailures] = useState(0);
    const [editingRate, setEditingRate] = useState(null); // { currency, value } while HR / Finance / Admin type a new rate
    const [rateError, setRateError] = useState('');
    const [savingRate, setSavingRate] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [approvalComment, setApprovalComment] = useState('');
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [approvalAction, setApprovalAction] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeciding, setIsDeciding] = useState(false);
    const [approvalError, setApprovalError] = useState('');

    const [baseCurrency, setBaseCurrency] = useState('INR');
    const [, setOrgName] = useState('PATHtoIP');

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await api.get('/auth/config');
                setBaseCurrency(res.data.baseCurrency || 'INR');
                setOrgName(res.data.orgName || 'PATHtoIP');
            } catch (err) {
                console.error('Failed to fetch config', err);
            }
        };
        fetchConfig();
    }, []);

    useEffect(() => {
        if (id) {
            loadReport(id);
            loadAuditTrail(id);
        }
    }, [id]);

    // Back to the list it was opened from: an approver returns to Pending Approvals, not to their own reports.
    const backTo = location.state?.from === 'approvals' ? '/tools/expenses?tab=approvals' : '/tools/expenses';

    // "Bulk import" on a report that was not saved yet saves it first, then lands here with the dialog requested.
    useEffect(() => {
        if (id && report.id === id && location.state?.bulk) {
            setShowBulk(true);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [id, report.id, location.state, location.pathname, navigate]);

    const loadReport = async (reportId) => {
        const existing = await getReport(reportId);
        if (existing) {
            setReport(toUiReport(existing));
        } else {
            navigate(backTo);
        }
    };

    const isActiveImport = (b) => ['UPLOADING', 'QUEUED', 'RUNNING'].includes(b.status);

    // Receipts are read on the server. While that goes on, ask every 5 seconds how far it is; when it
    // finishes, load the new lines.
    // Only the lines and the total are refreshed: the title, dates and purpose being typed are not saved yet.
    const refreshItems = async (reportId) => {
        try {
            const fresh = await getReport(reportId);
            if (fresh) setReport(prev => withServerItems(prev, fresh));
        } catch (error) {
            console.error('Failed to refresh the lines', error);
        }
    };

    const loadImports = async (reportId) => {
        try {
            const list = await listImports(reportId);
            const wasActive = importsRef.current.some(isActiveImport);
            const readBefore = importsRef.current.reduce((n, b) => n + (b.done || 0), 0);
            const readNow = list.reduce((n, b) => n + (b.done || 0), 0);
            importsRef.current = list;
            setImports(list);
            setPollFailures(0);
            // New lines as they are read, and a last refresh when the batch ends.
            if (readNow !== readBefore || (wasActive && !list.some(isActiveImport))) refreshItems(reportId);
        } catch (error) {
            console.error('Failed to load receipt imports', error);
            setPollFailures(n => n + 1);
        }
    };

    useEffect(() => {
        if (id) loadImports(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const anyImportActive = imports.some(isActiveImport);
    useEffect(() => {
        if (!id || !anyImportActive) return undefined;
        const timer = setInterval(() => loadImports(id), 5000);
        return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, anyImportActive]);

    const handleCancelImport = async (batch) => {
        if (!window.confirm('Stop reading the receipts that are left? Lines already added stay on the report.')) return;
        try {
            await cancelImport(id, batch.id);
        } catch (error) {
            alert(error.response?.data?.error || 'Could not cancel the import.');
        }
        await loadImports(id);
        refreshItems(id);
    };

    const handleDismissImport = async (batch) => {
        setImports(prev => prev.filter(b => b.id !== batch.id));
        try {
            await dismissImport(id, batch.id);
        } catch (error) {
            console.error('Dismiss failed', error);
            alert('Could not dismiss the result. Please try again.');
            loadImports(id); // it is still there: show it again
        }
    };

    const handleSaveRate = async () => {
        if (savingRate) return;
        setRateError('');
        setSavingRate(true);
        try {
            const updated = await setExpenseRate(id, editingRate.currency, Number(editingRate.value));
            setReport(prev => withServerItems(prev, updated));
            setEditingRate(null);
            loadAuditTrail(id); // the change is in the approval history
        } catch (error) {
            setRateError(error.response?.data?.error || 'Could not change the rate. Please try again.');
        } finally {
            setSavingRate(false);
        }
    };

    const handleMarkChecked = async () => {
        try {
            const updated = await confirmChecked(id);
            setReport(prev => withServerItems(prev, updated));
        } catch (error) {
            alert(error.response?.data?.error || 'Could not mark the lines as checked.');
        }
    };

    const loadAuditTrail = async (reportId) => {
        try {
            const trails = await getAuditTrail(reportId);
            setAuditTrail(trails);
        } catch (error) {
            console.error('Failed to load audit trail', error);
        }
    };

    const handleSaveReport = async () => {
        if (!report.title) {
            alert('Please enter a report title');
            return;
        }

        setIsSaving(true);
        const apiData = {
            ...report,
            startDate: report.dateRange.start,
            endDate: report.dateRange.end
        };
        // A saved report's items change through their own requests. Sending the list back from here
        // could remove lines added meanwhile (another tab, a bulk import).
        if (id) delete apiData.items;

        try {
            const saved = await saveReport(apiData);
            if (saved && !id) {
                navigate(`/tools/expenses/${saved.id}`);
            } else if (saved) {
                loadReport(saved.id);
                // alert('Report saved successfully!');
            }
        } catch (error) {
            console.error('Save report error:', error);
            alert('Failed to save report. Please ensure all database migrations are applied.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkImport = async () => {
        if (report.id) {
            setShowBulk(true);
            return;
        }
        if (!report.title) {
            alert('Please enter a report title first');
            return;
        }
        // The files are added to a saved report, in small requests, so save the header first.
        setIsSaving(true);
        try {
            const saved = await saveReport({ ...report, startDate: report.dateRange.start, endDate: report.dateRange.end });
            if (saved) navigate(`/tools/expenses/${saved.id}`, { state: { bulk: true } });
        } catch (error) {
            console.error('Save report error:', error);
            alert(error.response?.data?.error || 'Failed to save the report. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddExpense = () => {
        setEditingItem(null);
        setShowItemForm(true);
    };

    const handleEditExpense = (item) => {
        setEditingItem(item);
        setShowItemForm(true);
    };

    const handleSaveExpense = async (itemData) => {
        try {
            if (report.id) {
                // One small request for this item; the receipt of the other items never travels.
                const updated = editingItem
                    ? await updateExpenseItem(report.id, editingItem.id, itemData)
                    : await addExpenseItem(report.id, itemData);
                setReport(prev => withServerItems(prev, updated));
            } else {
                // A report that is not saved yet keeps its items here until Save Report.
                const items = editingItem
                    ? report.items.map(i => (i.id === editingItem.id ? { ...i, ...itemData } : i))
                    : [...report.items, { ...itemData, id: Date.now().toString(), temp: true }];
                setReport(prev => ({ ...prev, items, totalAmount: sumItems(items) }));
            }
            setShowItemForm(false);
            setEditingItem(null);
        } catch (error) {
            console.error('Save expense item error:', error);
            // The form stays open so nothing typed or attached is lost.
            alert(error.response?.data?.error || 'Failed to save the expense. Please try again.');
        }
    };

    const handleDeleteExpense = async (itemId) => {
        if (!window.confirm('Delete this expense?')) return;
        const isLocal = !report.id || report.items.find(i => i.id === itemId)?.temp;
        if (isLocal) {
            const items = report.items.filter(item => item.id !== itemId);
            setReport(prev => ({ ...prev, items, totalAmount: sumItems(items) }));
            return;
        }
        try {
            const updated = await removeExpenseItem(report.id, itemId);
            setReport(prev => withServerItems(prev, updated));
        } catch (error) {
            console.error('Delete expense item error:', error);
            alert(error.response?.data?.error || 'Failed to delete the expense. Please try again.');
        }
    };

    const handleExportPDF = async () => {
        try {
            const response = await api.get(`/expenses/${id}/export`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${report.title?.replace(/\s+/g, '_') || 'ExpenseReport'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export PDF. Please try again later.');
        }
    };

    const handleSubmitForApproval = async () => {
        if (!window.confirm('Submit this report for approval? You cannot edit it afterwards.')) return;
        setIsSubmitting(true);
        try {
            const updated = await submitReport(id);
            if (updated) {
                loadReport(id);
                loadAuditTrail(id);
                alert('Report submitted successfully!');
            }
        } catch (error) {
            console.error('Submit report error:', error);
            alert(error.response?.data?.error || 'Failed to submit report. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleApprovalAction = async () => {
        if (isDeciding) return;
        setIsDeciding(true);
        setApprovalError('');
        try {
            const result = approvalAction === 'APPROVE'
                ? await approveReport(id, approvalComment)
                : await rejectReport(id, approvalComment);
            if (result) {
                setShowApprovalModal(false);
                setApprovalComment('');
            }
        } catch (error) {
            // 403/409 mean the report was decided or changed meanwhile: say so and refresh it.
            setApprovalError(error.response?.data?.error || 'Could not record your decision. Please try again.');
        } finally {
            setIsDeciding(false);
            loadReport(id);
            loadAuditTrail(id);
        }
    };

    const StatusBadge = ({ status }) => {
        const colors = {
            DRAFT: '#6b7280',
            SUBMITTED: '#eab308',
            PENDING_MANAGER: '#f59e0b',
            PENDING_FINANCE: '#3b82f6',
            APPROVED: '#22c55e',
            REJECTED: '#ef4444',
            PAID: '#10b981'
        };

        return (
            <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: 9999,
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'white',
                backgroundColor: colors[status] || '#6b7280'
            }}>
                {status?.replace('_', ' ')}
            </span>
        );
    };

    const canEdit = report.status === 'DRAFT' || report.status === 'REJECTED';
    const needsInput = (report.items || []).filter(i => i.reviewState === 'INPUT');
    const toCheck = (report.items || []).filter(i => i.reviewState === 'CHECK');
    const readingNow = imports.filter(b => b.status === 'QUEUED' || b.status === 'RUNNING');
    const unfinishedUploads = imports.filter(b => b.status === 'UPLOADING');
    const finished = imports.filter(b => b.status === 'DONE');
    // The server refuses to submit in these cases too; saying why here saves the round trip.
    const submitHint = anyImportActive ? 'Receipts are still being read. You can submit once they are done.'
        : needsInput.length ? `${needsInput.length} expense line${needsInput.length === 1 ? ' needs' : 's need'} your input before this can be submitted.` : '';

    // Get current user for role-based actions
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    // One rate per foreign currency, for the whole report. HR, Finance or an Admin change it until the report is approved,
    // but not on their own report (the server enforces this too).
    const usedRates = (report.rates || []).filter(r => (report.items || []).some(i => i.currency === r.currency));
    const canChangeRates = !!report.id && ['ADMIN', 'HR', 'FINANCE'].includes(currentUser.role) && report.userId !== currentUser.id
        && ['DRAFT', 'SUBMITTED', 'PENDING_MANAGER', 'PENDING_FINANCE', 'REJECTED'].includes(report.status);
    const rateNote = (r) => ({
        HISTORICAL: `rate of ${r.rateDate}`,
        LATEST: 'latest rate',
        PEG: r.rateDate ? `fixed AED rate, ${r.rateDate}` : 'fixed AED rate',
        FALLBACK: 'approximate: the rate service could not be reached. It is replaced by the official rate when the report is next saved, or HR / Finance can type it',
        LEGACY: 'rate saved with the first line',
        MANUAL: `set by ${r.setBy || 'HR / Finance'}`,
    })[r.source] || r.source;
    const isApprover = useMemo(() => {
        if (!report.status) return false;
        if (report.status === 'SUBMITTED') {
            return ['MANAGER', 'HR', 'ADMIN'].includes(currentUser.role);
        }
        if (report.status === 'PENDING_FINANCE') {
            return ['FINANCE', 'ADMIN'].includes(currentUser.role);
        }
        return false;
    }, [report.status, currentUser.role]);

    return (
        <div className="container section">
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <div style={{ marginBottom: '2rem' }}>
                    <button
                        onClick={() => navigate(backTo)}
                        className="btn btn-secondary"
                        style={{ marginBottom: '1rem' }}
                    >
                        <ArrowLeft size={16} style={{ marginRight: '0.5rem' }} />
                        Back to Reports
                    </button>

                    <h1 className="gradient-text" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
                        {id ? 'Edit Expense Report' : 'New Expense Report'}
                    </h1>
                </div>

                {/* Workflow Section */}
                {id && (
                    <div style={{
                        background: 'var(--surface)',
                        borderRadius: 'var(--radius)',
                        boxShadow: 'var(--shadow)',
                        padding: '2rem',
                        marginBottom: '2rem'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h3 style={{ marginBottom: '0.5rem' }}>Report Status</h3>
                                <StatusBadge status={report.status} />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                                {canEdit && (
                                    <button onClick={handleSubmitForApproval} disabled={isSubmitting || isSaving || !!submitHint} title={submitHint || undefined} className="btn btn-primary">
                                        {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
                                    </button>
                                )}

                                {canEdit && submitHint && <span role="status" style={{ color: 'var(--text-muted)', alignSelf: 'center', maxWidth: '22rem' }}>{submitHint}</span>}

                                {isApprover && (
                                    <>
                                        <button
                                            onClick={() => { setApprovalAction('APPROVE'); setShowApprovalModal(true); }}
                                            className="btn btn-primary"
                                            style={{ backgroundColor: '#22c55e' }}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => { setApprovalAction('REJECT'); setShowApprovalModal(true); }}
                                            className="btn btn-secondary"
                                            style={{ color: '#ef4444', borderColor: '#ef4444' }}
                                        >
                                            Reject
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {auditTrail.length > 0 && (
                            <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border)' }}>
                                <h4 style={{ marginBottom: '1rem' }}>Approval History</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {auditTrail.map((trail) => (
                                        <div key={trail.id} style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '0.5rem', fontSize: '0.9rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                                <span style={{ fontWeight: 600 }}>{trail.approver?.name} ({trail.approver?.role})</span>
                                                <span style={{ color: 'var(--text-muted)' }}>{new Date(trail.createdAt).toLocaleString()}</span>
                                            </div>
                                            <div style={{ color: trail.status === 'APPROVED' ? '#22c55e' : trail.status === 'REJECTED' ? '#ef4444' : '#6b7280' }}>
                                                Status: {trail.status}
                                            </div>
                                            {trail.comments && (
                                                <div style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
                                                    "{trail.comments}"
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Report Metadata */}
                <div style={{
                    background: 'var(--surface)',
                    borderRadius: 'var(--radius)',
                    boxShadow: 'var(--shadow)',
                    padding: '2rem',
                    marginBottom: '2rem'
                }}>
                    <h3 style={{ marginBottom: '1.5rem' }}>Report Details</h3>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Report Title *</label>
                        <input
                            type="text"
                            value={report.title}
                            onChange={(e) => setReport(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="e.g., Business Trip - NYC"
                            disabled={!canEdit}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem'
                            }}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Start Date</label>
                            <input
                                type="date"
                                value={report.dateRange.start}
                                onChange={(e) => setReport(prev => ({
                                    ...prev,
                                    dateRange: { ...prev.dateRange, start: e.target.value }
                                }))}
                                disabled={!canEdit}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius)',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>End Date</label>
                            <input
                                type="date"
                                value={report.dateRange.end}
                                onChange={(e) => setReport(prev => ({
                                    ...prev,
                                    dateRange: { ...prev.dateRange, end: e.target.value }
                                }))}
                                disabled={!canEdit}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius)',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Purpose</label>
                        <textarea
                            value={report.purpose}
                            onChange={(e) => setReport(prev => ({ ...prev, purpose: e.target.value }))}
                            placeholder="e.g., Client meeting"
                            rows={3}
                            disabled={!canEdit}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    {canEdit && (
                        <button onClick={handleSaveReport} disabled={isSaving || isSubmitting} className="btn btn-primary">
                            <Save size={16} style={{ marginRight: '0.5rem' }} />
                            {isSaving ? 'Saving...' : 'Save Report'}
                        </button>
                    )}
                </div>

                {/* Expenses Section */}
                <div style={{
                    background: 'var(--surface)',
                    borderRadius: 'var(--radius)',
                    boxShadow: 'var(--shadow)',
                    padding: '2rem'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{ margin: 0 }}>Expenses ({report.items?.length || 0})</h3>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            {canEdit && (
                                <button onClick={handleBulkImport} disabled={isSaving || anyImportActive} title={anyImportActive ? 'Receipts are already being read for this report' : undefined} className="btn btn-secondary">
                                    <Images size={16} style={{ marginRight: '0.5rem' }} />
                                    Bulk import receipts
                                </button>
                            )}
                            {canEdit && (
                                <button onClick={handleAddExpense} className="btn btn-primary">
                                    <Plus size={16} style={{ marginRight: '0.5rem' }} />
                                    Add Expense
                                </button>
                            )}
                            {report.items?.length > 0 && (
                                <button onClick={handleExportPDF} className="btn btn-secondary">
                                    <Download size={16} style={{ marginRight: '0.5rem' }} />
                                    Export PDF
                                </button>
                            )}
                        </div>
                    </div>

                    {usedRates.length > 0 && (
                        <div data-testid="exchange-rates" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--card-bg)' }}>
                            <strong>Exchange rate{usedRates.length === 1 ? '' : 's'} for this report</strong>
                            <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem', fontSize: '0.9rem' }}>one rate per currency, used for every line</span>
                            <ul style={{ listStyle: 'none', margin: '0.5rem 0 0', padding: 0 }}>
                                {usedRates.map(r => (
                                    <li key={r.currency} data-testid={`rate-${r.currency}`} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', padding: '0.25rem 0' }}>
                                        {editingRate?.currency === r.currency ? (
                                            <>
                                                <label>1 {r.currency} ={' '}
                                                    <input aria-label={`Rate for ${r.currency}`} inputMode="decimal" value={editingRate.value} onChange={(e) => setEditingRate({ currency: r.currency, value: e.target.value })} style={{ width: '8rem', padding: '0.25rem 0.5rem' }} />
                                                    {' '}{r.baseCurrency}
                                                </label>
                                                <button type="button" className="btn btn-primary" onClick={handleSaveRate} disabled={savingRate}>{savingRate ? 'Saving…' : 'Save rate'}</button>
                                                <button type="button" className="btn btn-secondary" onClick={() => { setEditingRate(null); setRateError(''); }} disabled={savingRate}>Cancel</button>
                                            </>
                                        ) : (
                                            <>
                                                <span><strong>1 {r.currency} = {Number(r.rate).toLocaleString(undefined, { maximumFractionDigits: 6 })} {r.baseCurrency}</strong></span>
                                                <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{rateNote(r)}</span>
                                                {canChangeRates && <button type="button" className="btn btn-secondary" disabled={!!editingRate} onClick={() => { setEditingRate({ currency: r.currency, value: String(r.rate) }); setRateError(''); }}>Change</button>}
                                                {r.mixed && <span role="status" style={{ color: '#b45309', fontSize: '0.9rem' }}>Some lines in {r.currency} were converted at other rates (this report is from before there was one rate per report).{canChangeRates ? ' Press Change and save to put them all on this rate.' : ''}</span>}
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            {rateError && <p role="alert" style={{ color: '#dc2626', margin: '0.5rem 0 0' }}>{rateError}</p>}
                        </div>
                    )}
                    {pollFailures >= 2 && anyImportActive && (
                        <p role="status" style={{ color: '#b45309', marginBottom: '1rem' }}>Could not check the progress of the receipts. Trying again; what you see may be out of date.</p>
                    )}
                    {unfinishedUploads.map(batch => (
                        <div key={batch.id} role="status" data-testid="import-unfinished" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid #eab308', borderRadius: 'var(--radius)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <span><strong>An upload was started but not finished.</strong> Stop it to submit this report, or choose the files again.</span>
                            <button type="button" className="btn btn-secondary" onClick={() => handleCancelImport(batch)}>Stop</button>
                        </div>
                    ))}
                    {readingNow.map(batch => (
                        <div key={batch.id} role="status" data-testid="import-progress" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--card-bg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <span>
                                <strong>Reading receipts: {batch.done} of {batch.total}.</strong>{' '}
                                You can leave this page; we will email you when they are ready. The lines appear here as they are read.
                            </span>
                            <button type="button" className="btn btn-secondary" onClick={() => handleCancelImport(batch)}>Stop</button>
                        </div>
                    ))}
                    {finished.map(batch => (
                        <div key={batch.id} data-testid="import-result" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--card-bg)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                                <strong>
                                    Receipts read: {batch.added} line{batch.added === 1 ? '' : 's'} added
                                    {batch.needsInput ? `, ${batch.needsInput} need your input` : ''}
                                    {batch.failed ? `, ${batch.failed} could not be used` : ''}.
                                </strong>
                                <button type="button" className="btn btn-secondary" onClick={() => handleDismissImport(batch)}>Dismiss</button>
                            </div>
                            {batch.failedFiles?.length > 0 && (
                                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', color: '#b45309', fontSize: '0.9rem' }}>
                                    {batch.failedFiles.map((f, i) => <li key={i}>{f.fileName}: {f.error}</li>)}
                                </ul>
                            )}
                        </div>
                    ))}
                    {needsInput.length > 0 && (
                        <div role="alert" data-testid="needs-input" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid #ef4444', borderRadius: 'var(--radius)' }}>
                            <strong>Needs your input ({needsInput.length})</strong>
                            <ul style={{ margin: '0.5rem 0 0', paddingLeft: 0, listStyle: 'none' }}>
                                {needsInput.map(item => (
                                    <li key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.25rem 0' }}>
                                        <span>{item.merchant || 'Receipt'}: {item.reviewNotes}</span>
                                        {canEdit && <button type="button" className="btn btn-secondary" onClick={() => handleEditExpense(item)}>Fill in</button>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {canEdit && toCheck.length > 0 && (
                        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid #eab308', borderRadius: 'var(--radius)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <span><strong>{toCheck.length} line{toCheck.length === 1 ? '' : 's'} read from receipts.</strong> The reading can be wrong, so check the amount and date of each.</span>
                            <button type="button" className="btn btn-secondary" onClick={handleMarkChecked}>Mark all as checked</button>
                        </div>
                    )}

                    {report.items?.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
                            <p>No expenses added yet</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: '1.5rem' }}>
                                {report.items.map((item) => (
                                    <div key={item.id} data-review={item.reviewState} style={{
                                        padding: '1rem', border: `1px solid ${item.reviewState === 'INPUT' ? '#ef4444' : item.reviewState === 'CHECK' ? '#eab308' : 'var(--border)'}`, borderRadius: 'var(--radius)',
                                        marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600, display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                {item.merchant || <em style={{ fontWeight: 400 }}>No shop name</em>}
                                                {item.reviewState === 'INPUT' && <span style={{ fontSize: '0.75rem', color: '#fff', background: '#ef4444', padding: '2px 8px', borderRadius: 9999 }}>Needs your input</span>}
                                                {item.reviewState === 'CHECK' && <span style={{ fontSize: '0.75rem', color: '#422006', background: '#fde047', padding: '2px 8px', borderRadius: 9999 }}>Read from receipt: check</span>}
                                            </div>
                                            <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <span>{item.category || 'No category'}</span>
                                                <span>•</span>
                                                <span>{item.date ? item.date.split('T')[0] : ''}</span>
                                                {item.hasReceipt && report.id && (
                                                    <>
                                                        <span>•</span>
                                                        <a href={receiptHref(report.id, item.id)} target="_blank" rel="noreferrer">Receipt</a>
                                                    </>
                                                )}
                                                <span>•</span>
                                                <span style={{ fontWeight: 600 }}>{Number(item.amount) > 0 ? `${item.currency} ${parseFloat(item.amount).toFixed(2)}` : 'No amount'}</span>
                                                {item.currency !== (item.baseCurrency || baseCurrency) && item.convertedAmount && (
                                                    <span style={{ fontSize: '0.8rem', fontStyle: 'italic', background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: '4px' }}>
                                                        (≈ {item.baseCurrency || baseCurrency} {parseFloat(item.convertedAmount).toFixed(2)})
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {canEdit && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button onClick={() => handleEditExpense(item)} className="btn btn-secondary" style={{ padding: '0.5rem' }}><Edit2 size={16} /></button>
                                                <button onClick={() => handleDeleteExpense(item.id)} className="btn btn-secondary" style={{ padding: '0.5rem', color: '#ef4444' }}><Trash2 size={16} /></button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div style={{
                                padding: '1rem', background: 'var(--card-bg)', borderRadius: 'var(--radius)',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '1.1rem'
                            }}>
                                <span>Total Amount:</span>
                                <span style={{ color: 'var(--primary)' }}>
                                    {report.currency || baseCurrency} {parseFloat(report.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {canEdit && id && report.items.length > 0 && (
                    <div style={{ marginTop: '2rem', textAlign: 'right' }}>
                        {submitHint && <p role="status" style={{ color: 'var(--text-muted)', marginBottom: '0.5rem' }}>{submitHint}</p>}
                        <button onClick={handleSubmitForApproval} disabled={isSubmitting || isSaving || !!submitHint} className="btn btn-primary" style={{ padding: '0.75rem 2rem' }}>
                            {isSubmitting ? 'Submitting...' : 'Submit Report for Approval'}
                        </button>
                    </div>
                )}
            </div>

            {/* Approval Modal */}
            {showApprovalModal && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000
                }}>
                    <div style={{ backgroundColor: 'var(--surface)', borderRadius: '1rem', width: '100%', maxWidth: '24rem', padding: '1.5rem' }}>
                        <h3 style={{ marginBottom: '1rem' }}>{approvalAction === 'APPROVE' ? 'Approve' : 'Reject'} Report</h3>
                        <textarea
                            value={approvalComment}
                            onChange={(e) => setApprovalComment(e.target.value)}
                            style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border)', minHeight: '80px', marginBottom: '1.5rem' }}
                            placeholder="Optional comments..."
                        />
                        {approvalError && <p role="alert" style={{ color: '#ef4444', marginBottom: '1rem' }}>{approvalError}</p>}
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button onClick={() => { setShowApprovalModal(false); setApprovalError(''); }} disabled={isDeciding} className="btn btn-secondary">Cancel</button>
                            <button onClick={handleApprovalAction} disabled={isDeciding} className="btn btn-primary" style={{ backgroundColor: approvalAction === 'APPROVE' ? '#22c55e' : '#ef4444' }}>
                                {isDeciding ? 'Saving...' : `Confirm ${approvalAction === 'APPROVE' ? 'Approval' : 'Rejection'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showBulk && (
                <ReceiptImportDialog
                    report={report}
                    onClose={() => setShowBulk(false)}
                    onStarted={() => loadImports(id)}
                />
            )}

            {showItemForm && (
                <ExpenseItemForm
                    item={editingItem}
                    receiptHref={editingItem?.hasReceipt && report.id ? receiptHref(report.id, editingItem.id) : null}
                    rates={report.rates || []}
                    endDate={(report.id && report.endDate ? String(report.endDate).split('T')[0] : report.dateRange?.end) || ''}
                    onSave={handleSaveExpense}
                    onCancel={() => { setShowItemForm(false); setEditingItem(null); }}
                />
            )}
        </div>
    );
};

export default ExpenseReportForm;
